import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useActivity } from '../App';

const api = () => axios.create({
  baseURL: 'http://localhost:8080',
  headers: { Authorization: `Bearer ${localStorage.getItem('wb_admin_token')}` },
});

const TYPE_CFG = {
  DAILY:  { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)'  },
  WEEKLY: { color: '#6366f1', bg: 'rgba(99,102,241,0.12)'  },
};

const CTYPE_CFG = {
  WALK_KM:     { color: '#06b6d4', bg: 'rgba(6,182,212,0.12)',   label: 'Walk KM',     unit: 'km'      },
  BATTLE_JOIN: { color: '#ef4444', bg: 'rgba(239,68,68,0.12)',   label: 'Battle Join', unit: 'battles' },
  STEPS:       { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   label: 'Steps',       unit: 'steps'   },
  STREAK:      { color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)', label: 'Streak',      unit: 'days'    },
};

const EMPTY_FORM = {
  icon: '🎯', title: '', description: '',
  type: 'DAILY', challengeType: 'WALK_KM',
  targetValue: '', rewardCoins: '', rewardXp: '',
  activeDate: '', active: true,
};

const Challenges = () => {
  const { addLog } = useActivity();
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('ALL');
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // null = create, id = edit
  const [form, setForm]         = useState({ ...EMPTY_FORM });
  const [saving, setSaving]     = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api().get('/api/admin/challenges');
      setData(res.data?.data || []);
    } catch { toast.error('Failed to load challenges'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY_FORM });
    setShowModal(true);
  };

  const openEdit = (ch) => {
    setEditTarget(ch.id);
    setForm({
      icon:          ch.icon          || '🎯',
      title:         ch.title         || '',
      description:   ch.description   || '',
      type:          ch.type          || 'DAILY',
      challengeType: ch.challengeType || 'WALK_KM',
      targetValue:   ch.targetValue   ?? '',
      rewardCoins:   ch.rewardCoins   ?? '',
      rewardXp:      ch.rewardXp      ?? '',
      activeDate:    ch.activeDate     ? ch.activeDate.slice(0, 10) : '',
      active:        ch.active        ?? true,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        targetValue:  Number(form.targetValue),
        rewardCoins:  Number(form.rewardCoins),
        rewardXp:     Number(form.rewardXp),
      };
      if (editTarget) {
        await api().put(`/api/admin/challenges/${editTarget}`, body);
        toast.success('Challenge updated');
        addLog && addLog(`Challenge #${editTarget} updated`);
      } else {
        await api().post('/api/admin/challenges', body);
        toast.success('Challenge created');
        addLog && addLog(`New challenge "${form.title}" created`);
      }
      setShowModal(false);
      loadData();
    } catch { toast.error('Failed to save challenge'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (ch) => {
    try {
      await api().put(`/api/admin/challenges/${ch.id}/toggle`);
      toast.success(`Challenge ${ch.active ? 'deactivated' : 'activated'}`);
      addLog && addLog(`Challenge #${ch.id} toggled`);
      loadData();
    } catch { toast.error('Failed to toggle challenge'); }
  };

  const handleDelete = async (id) => {
    try {
      await api().delete(`/api/admin/challenges/${id}`);
      toast.success('Challenge deleted');
      addLog && addLog(`Challenge #${id} deleted`);
      setDeleteConfirm(null);
      loadData();
    } catch { toast.error('Failed to delete challenge'); }
  };

  const filtered = data.filter(ch => {
    if (filter === 'ALL')     return true;
    if (filter === 'DAILY')   return ch.type === 'DAILY';
    if (filter === 'WEEKLY')  return ch.type === 'WEEKLY';
    if (filter === 'ACTIVE')  return ch.active;
    if (filter === 'INACTIVE')return !ch.active;
    return true;
  });

  const totalCount    = data.length;
  const activeDailyCount  = data.filter(c => c.active && c.type === 'DAILY').length;
  const activeWeeklyCount = data.filter(c => c.active && c.type === 'WEEKLY').length;

  const FILTER_TABS = [
    { key: 'ALL',      label: 'All',      count: data.length },
    { key: 'DAILY',    label: 'Daily',    count: data.filter(c => c.type === 'DAILY').length },
    { key: 'WEEKLY',   label: 'Weekly',   count: data.filter(c => c.type === 'WEEKLY').length },
    { key: 'ACTIVE',   label: 'Active',   count: data.filter(c => c.active).length },
    { key: 'INACTIVE', label: 'Inactive', count: data.filter(c => !c.active).length },
  ];

  return (
    <div className="animate-in">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
            Gamification / <span style={{ color: 'var(--text-secondary)' }}>Challenges</span>
          </div>
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 800 }}>🎯 Challenges</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            Create and manage daily & weekly challenges for users
          </p>
        </div>
        <button style={createBtnS} onClick={openCreate}>➕ Create Challenge</button>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '14px', marginBottom: '24px' }}>
        {[
          { label: 'Total Challenges',     value: totalCount,        color: 'var(--accent-primary)', icon: '🎯' },
          { label: 'Active Daily',         value: activeDailyCount,  color: '#f59e0b',               icon: '☀️' },
          { label: 'Active Weekly',        value: activeWeeklyCount, color: '#6366f1',               icon: '📅' },
        ].map((s, i) => (
          <div key={i} style={cardS}>
            <div style={{ fontSize: '1.4rem' }}>{s.icon}</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: s.color, marginTop: '4px' }}>{s.value}</div>
            <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {FILTER_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            style={{
              padding: '7px 14px', borderRadius: '8px', border: '1px solid',
              cursor: 'pointer', fontSize: '0.84rem', fontWeight: 600, transition: 'all 0.15s',
              borderColor: filter === tab.key ? 'var(--accent-primary)' : 'var(--border-color)',
              background: filter === tab.key ? 'rgba(99,102,241,0.15)' : 'var(--bg-tertiary)',
              color: filter === tab.key ? 'var(--accent-primary)' : 'var(--text-secondary)',
            }}
          >
            {tab.label}
            <span style={{ marginLeft: '6px', fontSize: '0.72rem', padding: '1px 6px', borderRadius: '10px',
              background: filter === tab.key ? 'rgba(99,102,241,0.2)' : 'var(--bg-hover)',
              color: filter === tab.key ? 'var(--accent-primary)' : 'var(--text-muted)' }}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Challenge Cards Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>No challenges found</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '16px' }}>
          {filtered.map(ch => {
            const typeCfg  = TYPE_CFG[ch.type]          || TYPE_CFG.DAILY;
            const ctypeCfg = CTYPE_CFG[ch.challengeType] || CTYPE_CFG.WALK_KM;
            return (
              <div key={ch.id} style={{
                background: 'var(--bg-card)',
                border: `1px solid var(--border-color)`,
                borderLeft: `4px solid ${ch.active ? '#22c55e' : '#475569'}`,
                borderRadius: '12px',
                padding: '20px',
                position: 'relative',
                transition: 'box-shadow 0.15s',
              }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.2)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                {/* Top row: icon + title + type badges */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ fontSize: '2rem', lineHeight: 1 }}>{ch.icon || '🎯'}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 800, fontSize: '1rem' }}>{ch.title}</span>
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
                        background: typeCfg.bg, color: typeCfg.color }}>
                        {ch.type}
                      </span>
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
                        background: ctypeCfg.bg, color: ctypeCfg.color }}>
                        {ctypeCfg.label}
                      </span>
                    </div>
                    {ch.description && (
                      <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                        {ch.description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Stats row */}
                <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.74rem' }}>Target: </span>
                    <strong>{ch.targetValue} {ctypeCfg.unit}</strong>
                  </span>
                  <span style={{ fontSize: '0.82rem' }}>
                    🪙 <strong style={{ color: '#f59e0b' }}>{ch.rewardCoins}</strong> coins
                  </span>
                  <span style={{ fontSize: '0.82rem' }}>
                    ⭐ <strong style={{ color: '#818cf8' }}>{ch.rewardXp}</strong> XP
                  </span>
                  {ch.activeDate && (
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      📅 {new Date(ch.activeDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  )}
                </div>

                {/* Bottom row: status + actions */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                  <button
                    onClick={() => handleToggle(ch)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '5px 12px', borderRadius: '20px', border: '1px solid',
                      cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, transition: 'all 0.15s',
                      borderColor: ch.active ? '#22c55e' : 'var(--border-color)',
                      background: ch.active ? 'rgba(34,197,94,0.1)' : 'var(--bg-tertiary)',
                      color: ch.active ? '#22c55e' : 'var(--text-muted)',
                    }}
                  >
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: ch.active ? '#22c55e' : '#475569', display: 'inline-block' }} />
                    {ch.active ? 'Active' : 'Inactive'}
                  </button>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button style={editBtnS} onClick={() => openEdit(ch)}>✏️ Edit</button>
                    <button style={deleteBtnS} onClick={() => setDeleteConfirm(ch)}>🗑️ Delete</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div style={overlayS} onClick={() => setShowModal(false)}>
          <div style={{ ...modalS, maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px', fontSize: '1.15rem', fontWeight: 800 }}>
              {editTarget ? '✏️ Edit Challenge' : '➕ Create Challenge'}
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={labelS}>Icon</label>
                <input style={{ ...inputS, textAlign: 'center', fontSize: '1.4rem' }}
                  value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} maxLength={4} />
              </div>
              <div>
                <label style={labelS}>Title *</label>
                <input style={inputS} placeholder="Challenge title…"
                  value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={labelS}>Description</label>
              <textarea style={{ ...inputS, resize: 'vertical' }} rows={2}
                placeholder="Brief description…"
                value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={labelS}>Type</label>
                <select style={inputS} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                  <option value="DAILY">DAILY</option>
                  <option value="WEEKLY">WEEKLY</option>
                </select>
              </div>
              <div>
                <label style={labelS}>Challenge Type</label>
                <select style={inputS} value={form.challengeType} onChange={e => setForm(f => ({ ...f, challengeType: e.target.value }))}>
                  <option value="WALK_KM">WALK_KM</option>
                  <option value="BATTLE_JOIN">BATTLE_JOIN</option>
                  <option value="STEPS">STEPS</option>
                  <option value="STREAK">STREAK</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={labelS}>Target Value</label>
                <input style={inputS} type="number" min="0" placeholder="0"
                  value={form.targetValue} onChange={e => setForm(f => ({ ...f, targetValue: e.target.value }))} />
              </div>
              <div>
                <label style={labelS}>Reward Coins 🪙</label>
                <input style={inputS} type="number" min="0" placeholder="0"
                  value={form.rewardCoins} onChange={e => setForm(f => ({ ...f, rewardCoins: e.target.value }))} />
              </div>
              <div>
                <label style={labelS}>Reward XP ⭐</label>
                <input style={inputS} type="number" min="0" placeholder="0"
                  value={form.rewardXp} onChange={e => setForm(f => ({ ...f, rewardXp: e.target.value }))} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', marginBottom: '20px', alignItems: 'end' }}>
              <div>
                <label style={labelS}>Active Date</label>
                <input style={inputS} type="date"
                  value={form.activeDate} onChange={e => setForm(f => ({ ...f, activeDate: e.target.value }))} />
              </div>
              <div style={{ paddingBottom: '2px' }}>
                <label style={labelS}>Active</label>
                <div>
                  <button
                    onClick={() => setForm(f => ({ ...f, active: !f.active }))}
                    style={{
                      padding: '8px 18px', borderRadius: '8px', border: '1px solid',
                      cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700, transition: 'all 0.15s',
                      borderColor: form.active ? '#22c55e' : 'var(--border-color)',
                      background: form.active ? 'rgba(34,197,94,0.12)' : 'var(--bg-tertiary)',
                      color: form.active ? '#22c55e' : 'var(--text-muted)',
                    }}
                  >
                    {form.active ? '✓ Active' : '○ Inactive'}
                  </button>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button style={cancelBtnS} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={{ ...primaryBtnS, opacity: saving ? 0.7 : 1 }} disabled={saving} onClick={handleSave}>
                {saving ? 'Saving…' : (editTarget ? '✏️ Update Challenge' : '➕ Create Challenge')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div style={overlayS} onClick={() => setDeleteConfirm(null)}>
          <div style={{ ...modalS, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 10px', fontSize: '1.1rem' }}>🗑️ Delete Challenge</h3>
            <p style={{ margin: '0 0 20px', fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
              Are you sure you want to delete <strong>"{deleteConfirm.title}"</strong>? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button style={cancelBtnS} onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button style={{ ...primaryBtnS, background: 'linear-gradient(135deg,#ef4444,#dc2626)' }}
                onClick={() => handleDelete(deleteConfirm.id)}>
                🗑️ Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const cardS      = { background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '18px', textAlign: 'center' };
const createBtnS = { padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white' };
const editBtnS   = { padding: '5px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' };
const deleteBtnS = { padding: '5px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, background: 'rgba(239,68,68,0.12)', color: '#ef4444' };
const primaryBtnS= { padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white' };
const cancelBtnS = { padding: '9px 18px', borderRadius: '8px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.88rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' };
const overlayS   = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalS     = { background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '28px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.4)' };
const labelS     = { display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '4px', textTransform: 'uppercase' };
const inputS     = { width: '100%', padding: '9px 12px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box' };

export default Challenges;
